import { Node } from 'reactflow';
import {
  OperationalModel,
  OperationalStatus,
  ReportInclusion,
  ReportInclusionSubLayers,
  SubLayersWithoutValidStatus,
  SubLayersWithScopeFilterOnly,
  SubLayerTypes,
} from '@constants/canvas/layers';
import {
  CiaFilters,
  ProjectFiltersContextProps,
} from '@context/ProjectFilters/types';
import { NodeDTO } from '@store/services/nodes/types';
import { isCriteriaFieldsMatch } from '@utils/canvasHelpers';
import { checkScopeFilter, ScopeType } from '@utils/domains';

/**
 * Every category the shared predicate evaluates: the filter-context
 * categories plus `domains` (Scope), `cis` (CIS-18 reference) and
 * `reportInclusion` (Report inclusion), none of which is a field of
 * `ProjectFiltersContextProps`, and the `scopeType` Scope is matched under —
 * Target when absent.
 */
export type ModelFilters = Pick<
  ProjectFiltersContextProps,
  | 'validationStatus'
  | 'reviewStatus'
  | 'operationalStatuses'
  | 'operatingModelStatuses'
  | 'partners'
  | 'ciaFilters'
> & {
  domains: string[];
  scopeType?: ScopeType;
  cis: string[];
  reportInclusion: string[];
};

/**
 * The slice of a node DTO the predicate reads. Exported so that anything
 * deriving per-value facts from the same fields — the popover's counts — is
 * typed against the exact paths the filter itself uses.
 */
export type FilterableNodeDto = Partial<
  Pick<
    NodeDTO,
    | 'type'
    | 'valid_status'
    | 'review_required'
    | 'partners'
    | 'cia'
    | 'cis'
    | 'is_reported'
    | 'domainNodes'
  >
> & {
  meta_data?: {
    operational_status?: OperationalStatus;
    operational_model?: OperationalModel;
  };
};

const hasCiaSelection = (ciaFilters: CiaFilters) =>
  Object.values(ciaFilters).some((fields) =>
    Object.values(fields).some((answers) => answers?.length),
  );

/**
 * The Report inclusion value an entity carries. Only a flag stored as off
 * reads as Not included; a flag never stored reads as Included, which is how
 * the drawer's switch and the report itself treat it.
 */
const reportInclusionOf = (dto: FilterableNodeDto | undefined) =>
  dto?.is_reported === false
    ? ReportInclusion.NotIncluded
    : ReportInclusion.Included;

/**
 * Whether an entity passes the Report inclusion category: always while the
 * category is empty, otherwise only an entity of a sub-layer in
 * `ReportInclusionSubLayers` whose value is one of those selected.
 */
const matchesReportInclusion = (
  dto: FilterableNodeDto | undefined,
  subLayer: SubLayerTypes | undefined,
  reportInclusion: string[],
) =>
  !reportInclusion.length ||
  (!!subLayer &&
    ReportInclusionSubLayers.includes(subLayer) &&
    reportInclusion.includes(reportInclusionOf(dto)));

/**
 * Whether any category other than Scope and Report inclusion carries a
 * selection.
 *
 * Both are left out on purpose: the sub-layers in
 * `SubLayersWithScopeFilterOnly` answer to Scope and Report inclusion alone
 * and are hidden wholesale once any other filter is active, so this is the
 * test that rule needs.
 */
export const hasActiveModelFilter = ({
  validationStatus,
  reviewStatus,
  operationalStatuses,
  operatingModelStatuses,
  partners,
  cis,
  ciaFilters,
}: ModelFilters) =>
  Boolean(
    validationStatus.length ||
      reviewStatus.length ||
      operationalStatuses.length ||
      operatingModelStatuses.length ||
      partners.length ||
      cis.length ||
      hasCiaSelection(ciaFilters),
  );

/**
 * The one client-side filter predicate: AND across categories, OR within a
 * category, a node with no value for an active category is hidden.
 *
 * Scope is evaluated first and for every sub-layer, so the scope-only
 * sub-layers are scoped like everything else before the "any other filter
 * hides them" rule runs. It goes through `checkScopeFilter` under the
 * selection's `scopeType`, which reads the one scope field that type names,
 * hides an entity type that does not carry it, and treats the Unassigned id
 * as a Scope value in its own right.
 *
 * Report inclusion comes next, also ahead of that rule and outside it: it
 * reads the entity's `is_reported` flag through `reportInclusionOf`, keeps
 * only the sub-layers in `ReportInclusionSubLayers`, and does not count as
 * "any other filter", so a Change initiative stays visible under Report
 * inclusion alone and is hidden once another category joins it. The
 * questionnaire-result view hides unreported entities by a rule of its own,
 * in its own consumers.
 *
 * CIS-18 reference keeps an entity whose `cis` ids intersect the selected
 * catalogue ids: a Control id matches an entity that references the
 * Control itself, never one that references only its Safeguards, and a
 * Safeguard id matches on its own — there is no parent expansion in
 * either direction. An entity type that carries no `cis` field is hidden
 * while the category is active, which is what confines the category to
 * Services, Capabilities and Functions.
 *
 * The three CIA categories read the entity's `cia` fields through
 * `isCriteriaFieldsMatch`: inside one category its criteria AND and one
 * criteria's answers OR, a criteria with nothing ticked is left out, and
 * an entity with no CIA fields at all is hidden while any of the three is
 * active.
 */
export const matchesModelFiltersDto = (
  dto: FilterableNodeDto | undefined,
  subLayer: SubLayerTypes | undefined,
  filters: ModelFilters,
) => {
  const {
    domains,
    scopeType,
    validationStatus,
    reviewStatus,
    operationalStatuses,
    operatingModelStatuses,
    partners,
    cis,
    reportInclusion,
    ciaFilters,
  } = filters;

  if (
    domains.length &&
    !checkScopeFilter(domains, dto?.type, dto?.domainNodes, scopeType)
  ) {
    return false;
  }

  if (!matchesReportInclusion(dto, subLayer, reportInclusion)) {
    return false;
  }

  if (subLayer && SubLayersWithScopeFilterOnly.includes(subLayer)) {
    return !hasActiveModelFilter(filters);
  }

  if (reviewStatus.length && !dto?.review_required) return false;

  if (validationStatus.length) {
    const hasNoValidStatus =
      !!subLayer && SubLayersWithoutValidStatus.includes(subLayer);

    const validStatus = dto?.valid_status;

    if (
      hasNoValidStatus ||
      !validStatus ||
      !validationStatus.includes(validStatus)
    ) {
      return false;
    }
  }

  const operationalStatus = dto?.meta_data?.operational_status;

  if (
    operationalStatuses.length &&
    (!operationalStatus || !operationalStatuses.includes(operationalStatus))
  ) {
    return false;
  }

  const operatingModel = dto?.meta_data?.operational_model;

  if (
    operatingModelStatuses.length &&
    (!operatingModel || !operatingModelStatuses.includes(operatingModel))
  ) {
    return false;
  }

  if (
    partners.length &&
    !partners.some((partner) => dto?.partners?.includes(partner))
  ) {
    return false;
  }

  if (cis.length && !cis.some((id) => dto?.cis?.includes(id))) {
    return false;
  }

  if (hasCiaSelection(ciaFilters)) {
    const isAllMatch =
      isCriteriaFieldsMatch(
        ciaFilters.assurance_level,
        dto?.cia?.assurance_level?.criterias,
      ) &&
      isCriteriaFieldsMatch(
        ciaFilters.coverage,
        dto?.cia?.coverage?.criterias,
      ) &&
      isCriteriaFieldsMatch(
        ciaFilters.applicability,
        dto?.cia?.applicability?.criterias,
      );

    if (!isAllMatch) return false;
  }

  return true;
};

export const matchesModelFilters = (node: Node, filters: ModelFilters) =>
  matchesModelFiltersDto(
    node.data?.dto,
    node.parentNode as SubLayerTypes,
    filters,
  );
